import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { customersAPI, jobsAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, User, MapPin, Briefcase, FileText } from 'lucide-react';

export const EditJob = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [jobRes, custRes] = await Promise.all([
                    jobsAPI.get(id),
                    customersAPI.list()
                ]);
                const job = jobRes.data;
                setCustomers(custRes.data);
                setFormData({
                    customer_id: job.customer_id || '',
                    site_address: {
                        line1: job.site_address?.line1 || '',
                        line2: job.site_address?.line2 || '',
                        city: job.site_address?.city || '',
                        county: job.site_address?.county || '',
                        postcode: job.site_address?.postcode || ''
                    },
                    job_type: job.job_type || 'Plastering',
                    enquiry_source: job.enquiry_source || '',
                    labour_only: job.labour_only || false,
                    job_notes: job.job_notes || '',
                    access_notes: job.access_notes || '',
                    parking_notes: job.parking_notes || ''
                });
            } catch {
                toast.error('Failed to load job');
                navigate(`/jobs/${id}`);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, navigate]);

    const handleAddressChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            site_address: { ...prev.site_address, [field]: value }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.customer_id) { toast.error('Please select a customer'); return; }
        if (!formData.site_address.line1 || !formData.site_address.postcode) {
            toast.error('Site address and postcode are required'); return;
        }
        setSaving(true);
        try {
            await jobsAPI.update(id, formData);
            toast.success('Job updated!');
            navigate(`/jobs/${id}`);
        } catch {
            toast.error('Failed to update job');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !formData) {
        return (
            <Layout>
                <div className="space-y-4 max-w-2xl mx-auto">
                    <Skeleton className="h-10 w-48" />
                    <Skeleton className="h-40 rounded-xl" />
                    <Skeleton className="h-56 rounded-xl" />
                    <Skeleton className="h-48 rounded-xl" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/jobs/${id}`)}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            Edit Job
                        </h1>
                        <p className="text-slate-500 text-sm">Update job details</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Customer Selection */}
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                                <User className="h-5 w-5 text-red-600" />
                                Customer
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Select Customer *
                                </Label>
                                <Select
                                    value={formData.customer_id}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, customer_id: v }))}
                                >
                                    <SelectTrigger className="h-12 border-slate-300" data-testid="customer-select">
                                        <SelectValue placeholder="Choose a customer..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {customers.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.name}{c.phone && ` - ${c.phone}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Site Address */}
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-red-600" />
                                Site Address
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Address Line 1 *
                                </Label>
                                <Input
                                    value={formData.site_address.line1}
                                    onChange={(e) => handleAddressChange('line1', e.target.value)}
                                    placeholder="123 Main Street"
                                    className="h-12 border-slate-300"
                                    data-testid="address-line1"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Address Line 2
                                </Label>
                                <Input
                                    value={formData.site_address.line2}
                                    onChange={(e) => handleAddressChange('line2', e.target.value)}
                                    placeholder="Flat 2, Building Name"
                                    className="h-12 border-slate-300"
                                    data-testid="address-line2"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        City
                                    </Label>
                                    <Input
                                        value={formData.site_address.city}
                                        onChange={(e) => handleAddressChange('city', e.target.value)}
                                        placeholder="London"
                                        className="h-12 border-slate-300"
                                        data-testid="address-city"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Postcode *
                                    </Label>
                                    <Input
                                        value={formData.site_address.postcode}
                                        onChange={(e) => handleAddressChange('postcode', e.target.value.toUpperCase())}
                                        placeholder="SW1A 1AA"
                                        className="h-12 border-slate-300"
                                        data-testid="address-postcode"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Job Options */}
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                                <Briefcase className="h-5 w-5 text-red-600" />
                                Job Options
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Job Type */}
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Job Type
                                </Label>
                                <div className="flex gap-2" data-testid="job-type-selector">
                                    {['Plastering', 'Rendering', 'Both'].map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, job_type: type }))}
                                            data-testid={`job-type-${type.toLowerCase()}`}
                                            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold border transition-colors ${
                                                formData.job_type === type
                                                    ? type === 'Plastering' ? 'bg-blue-600 text-white border-blue-600'
                                                      : type === 'Rendering' ? 'bg-orange-500 text-white border-orange-500'
                                                      : 'bg-purple-600 text-white border-purple-600'
                                                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                                            }`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Enquiry Source */}
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Enquiry Source
                                </Label>
                                <Select
                                    value={formData.enquiry_source}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, enquiry_source: v }))}
                                >
                                    <SelectTrigger className="h-12 border-slate-300" data-testid="enquiry-source-select">
                                        <SelectValue placeholder="How did they find us?" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {['Social Media', 'Website', 'Printed Press', 'Word of Mouth', 'Other'].map((src) => (
                                            <SelectItem key={src} value={src}>{src}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* Labour Only */}
                            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div>
                                    <p className="text-sm font-semibold text-slate-800">Labour Only</p>
                                    <p className="text-xs text-slate-500">Materials supplied by client</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, labour_only: !prev.labour_only }))}
                                    data-testid="labour-only-toggle"
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.labour_only ? 'bg-red-600' : 'bg-slate-300'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.labour_only ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Notes */}
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                                <FileText className="h-5 w-5 text-red-600" />
                                Notes
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Job Notes
                                </Label>
                                <Textarea
                                    value={formData.job_notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, job_notes: e.target.value }))}
                                    placeholder="General notes about the job..."
                                    rows={2}
                                    className="border-slate-300"
                                    data-testid="job-notes"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Access Notes
                                </Label>
                                <Input
                                    value={formData.access_notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, access_notes: e.target.value }))}
                                    placeholder="Key safe code, contact on arrival..."
                                    className="h-12 border-slate-300"
                                    data-testid="access-notes"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Parking Notes
                                </Label>
                                <Input
                                    value={formData.parking_notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, parking_notes: e.target.value }))}
                                    placeholder="Street parking, permit required..."
                                    className="h-12 border-slate-300"
                                    data-testid="parking-notes"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Submit */}
                    <Button
                        type="submit"
                        disabled={saving || !formData.customer_id}
                        className="w-full h-14 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide shadow-sm text-lg"
                        data-testid="save-job-btn"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </form>
            </div>
        </Layout>
    );
};
